import React, { useCallback, useMemo, useContext } from 'react'
import OptionsMenu from '../../Common/OptionsMenu/OptionsMenu'
import SectionFormContainer from '../components/SectionFormContainer'
import BoardingWizardContext from './BoardingWizardContext'
import useBoardingVisit from '../../../hooks/useBoardingVisit'
import { BOARDING_FIELDS } from './config'
import { STANDARD_KENNEL_OPTIONS } from './constants'
import { formatNumsToDollars, FONT_WEIGHTS } from '../../../utils'
import { makeStyles, Checkbox } from '@material-ui/core'

const useStyles = makeStyles(() => ({
	checkBoxContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: '.5rem',
		marginTop: '1rem',
		fontSize: '0.875rem',
		fontWeight: FONT_WEIGHTS.SemiBold,
		'& .MuiCheckbox-root': {
			padding: 0
		}
	}
}))

const NightProgram = (props) => {
	const classes = useStyles()
	const {
		wizardContextProvider: { wizard_state, setWizardAttributeAndValues }
	} = useContext(BoardingWizardContext)
	const {
		attr,
		subtext = '',
		first_view_of_multiple = false,
		copy_to_all_attr,
		petNames,
		name,
		_id: pet_id
	} = props
	const { loading, boarding, facility } = useBoardingVisit()

	const title = useMemo(
		() =>
			`Choose a Night Program for ${
				wizard_state[copy_to_all_attr] === true || wizard_state[BOARDING_FIELDS.sharing_kennels] === true
					? petNames
					: name
			}`,
		[name, wizard_state, copy_to_all_attr]
	)

	const handleCopyToAll = useCallback(
		(e) => {
			if (e.target && e.target.checked !== undefined) {
				const newValues = { ...wizard_state }
				newValues[copy_to_all_attr] = e.target.checked
				newValues[BOARDING_FIELDS.copy_kennel_options_from_pet_id] = e.target.checked ? pet_id : ''
				setWizardAttributeAndValues(newValues)
			}
		},
		[wizard_state, setWizardAttributeAndValues, attr, copy_to_all_attr, pet_id]
	)

	const handleSelection = useCallback(
		(type) => {
			const newValues = { ...wizard_state }
			const { boarding_options, sharing_kennels } = wizard_state
			let newBoardingOptions = [...boarding_options]

			if (sharing_kennels) {
				newBoardingOptions = newBoardingOptions.map((option) => {
					return { ...option, kennel_type: type }
				})
			} else {
				const currentPetOption = newBoardingOptions.findIndex((option) => {
					return option.pet_id === pet_id && option.kennel_type !== type
				})
				if (currentPetOption > -1) {
					const updatedOption = {
						...newBoardingOptions[currentPetOption],
						kennel_type: type
					}
					newBoardingOptions[currentPetOption] = updatedOption
				}
			}

			newValues[attr] = newBoardingOptions
			setWizardAttributeAndValues(newValues)
		},
		[wizard_state, setWizardAttributeAndValues, attr, copy_to_all_attr]
	)

	const showCopyToAllCheckbox = useMemo(() => {
		return (
			first_view_of_multiple &&
			wizard_state[BOARDING_FIELDS.pets].length > 1 &&
			wizard_state[BOARDING_FIELDS.sharing_kennels] === false &&
			copy_to_all_attr &&
			copy_to_all_attr > ''
		)
	}, [first_view_of_multiple, wizard_state, copy_to_all_attr])

	const options = useMemo(() => {
		if (!boarding || !facility) {
			return []
		}

		const { kennel_options } = facility || []

		const availableOptions = kennel_options.filter((option) => STANDARD_KENNEL_OPTIONS.includes(option.type))
		const { sharing_kennels, boarding_options } = wizard_state

		const selectedOption = boarding_options.find((option) => option.pet_id === pet_id)

		const o = availableOptions.map((option) => {
			const { price, description, active, public_name, type } = option
			let title = public_name

			if (sharing_kennels) {
				title = `${title} (Sharing)`
			}

			if (price > 0) {
				title = `${title} +${formatNumsToDollars(price)} / Night`
			} else {
				title = `${title} - Free`
			}

			return {
				title,
				description,
				disabled: loading || !active || wizard_state[attr] === type,
				selected: selectedOption['kennel_type'] === type,
				onSelected: () => handleSelection(type)
			}
		})

		return o
	}, [handleSelection, loading, wizard_state, attr, boarding, pet_id])

	return (
		<SectionFormContainer title={title} subtext={subtext}>
			<OptionsMenu options={options} />
			{showCopyToAllCheckbox ? (
				<div className={classes.checkBoxContainer}>
					<Checkbox onChange={handleCopyToAll} checked={wizard_state[copy_to_all_attr] === true} />
					<div>Copy schedule for all dogs</div>
				</div>
			) : null}
		</SectionFormContainer>
	)
}

export default NightProgram
